import { useEffect, useRef, useState } from 'react';
import type { OpenXAgentSidePanel } from '../../../context/OpenXAgentContext';
import { readStoredSize, writeStoredPosition } from '../../../context/openXAgentStorage';
import { fitViewportToSidePanel } from './geometry';
import type { ModalPosition, ModalSize } from './types';

export const SIDE_PANEL_ANIMATION_MS = 200;
export const SIDE_PANEL_TRANSITION = `width ${SIDE_PANEL_ANIMATION_MS}ms ease-out, height ${SIDE_PANEL_ANIMATION_MS}ms ease-out, left ${SIDE_PANEL_ANIMATION_MS}ms ease-out, top ${SIDE_PANEL_ANIMATION_MS}ms ease-out`;

type UseSidePanelTransitionParams = {
  sidePanel: OpenXAgentSidePanel | null;
  position: ModalPosition;
  size: ModalSize;
  setPosition: (position: ModalPosition) => void;
  setSize: (size: ModalSize) => void;
};

/**
 * Keeps modal geometry in sync with side-panel toggles: delays unmount for the exit animation,
 * persists the chat-only viewport after shrinking, and anchors the right edge when resizing.
 */
export const useSidePanelTransition = ({
  sidePanel,
  position,
  size,
  setPosition,
  setSize,
}: UseSidePanelTransitionParams): OpenXAgentSidePanel | null => {
  const hasSidePanel = sidePanel !== null;
  const [displayedSidePanel, setDisplayedSidePanel] = useState<OpenXAgentSidePanel | null>(sidePanel);
  const previousHasSidePanelRef = useRef(hasSidePanel);
  const previousSizeRef = useRef<ModalSize>(readStoredSize() ?? size);

  useEffect(() => {
    if (sidePanel !== null) {
      setDisplayedSidePanel(sidePanel);
      return undefined;
    }

    const timeoutId = setTimeout(() => setDisplayedSidePanel(null), SIDE_PANEL_ANIMATION_MS);

    return () => clearTimeout(timeoutId);
  }, [sidePanel]);

  useEffect(() => {
    if (sidePanel !== null && !previousHasSidePanelRef.current) previousSizeRef.current = size;
  }, [sidePanel, size]);

  useEffect(() => {
    const wasOpen = previousHasSidePanelRef.current;
    previousHasSidePanelRef.current = hasSidePanel;
    if (wasOpen === hasSidePanel) return;

    const transitionPosition = hasSidePanel
      ? position
      : { ...position, x: position.x + size.width - previousSizeRef.current.width };
    const fitted = fitViewportToSidePanel(
      { position: transitionPosition, size: previousSizeRef.current },
      hasSidePanel
    );

    // Persist the chat-only position; the stored size is left untouched so a reopened modal never
    // restores the side-panel width.
    if (!hasSidePanel) {
      writeStoredPosition(fitted.position);
    }

    if (fitted.size.width === size.width) return;

    setSize(fitted.size);
    setPosition(fitted.position);
  }, [hasSidePanel, position, size, setPosition, setSize]);

  return displayedSidePanel;
};
